import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { generatePath } from 'react-router-dom';
import { appPaths } from 'consts/paths/paths';
import { SpecialTopicAddEditForm } from 'components/templates';
import { journalTexts } from 'consts/text';
import { AddSpecialTopicFormFields } from 'types/forms/specialTopic';
import { parseAddSpecialTopicFormFieldsIntoAddSpecialTopicDTO } from 'utils/parsers/specialTopic/parseAddSpecialTopicFormFieldsIntoAddSpecialTopicDTO';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useUserDetails } from 'jotaiAtoms/userDetails';
import { useJournalAbilities } from 'abilities';
import { ErrorDisplay } from 'components/molecules';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useJournalDetailsBreadcrumb } from '../../../hooks';
import { useSpecialJournalTopicAddPageNetworkManage } from './hooks';

export const SpecialJournalTopicAddPage = () => {
    const { form } = useEditForm<AddSpecialTopicFormFields>();
    const { journalId } = useJournalId();
    const { id: userId, type: userType } = useUserDetails();
    const { displaySuccess } = useSimpleNotification();
    const { statefulNavigate } = useNavigateWithState();
    const { specialJournalsAbilities } = useJournalAbilities();

    const canAddOutOfTimetableTopics = specialJournalsAbilities.checkIfCanAddOutOfTimetableTopics();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'SPECIAL',
    });

    const onAddTopicSuccess = () => {
        displaySuccess(journalTexts.journalDetails.groupJournal.topics.topicAddSuccessNotification);
        statefulNavigate(
            generatePath(appPaths.app.journals.specialJournalDetails.pickedJournal.topics.base, {
                journalId,
            }),
            {
                replace: true,
            },
        );
    };

    const {
        addSpecialTopic,
        employeeData,
        addSpecialTopicError,
        isEmployeeDataError,
        isAddSpecialTopicLoading,
        specialLessonsData,
        handleSelectFormTopicDate,
        isSpecialLessonsDataFetching,
        isSpecialLessonsDataError,
    } = useSpecialJournalTopicAddPageNetworkManage({
        onAddTopicSuccess,
        journalId,
        userId,
        userType,
    });

    const onAddTopicFinish = (topicData: AddSpecialTopicFormFields) => {
        const parsedAddSpecialTopicDTO =
            parseAddSpecialTopicFormFieldsIntoAddSpecialTopicDTO(topicData);
        addSpecialTopic({
            journalId,
            addSpecialTopicDTO: parsedAddSpecialTopicDTO,
        });
    };

    if (canAddOutOfTimetableTopics) {
        const addOutOfTimetableTopicPathBase =
            appPaths.app.journals.specialJournalDetails.pickedJournal.topics[
                'add-out-of-timetable'
            ];
        statefulNavigate(generatePath(addOutOfTimetableTopicPathBase, { journalId }), {
            replace: true,
        });
    }

    if (isEmployeeDataError) {
        return <ErrorDisplay />;
    }

    return (
        <SpecialTopicAddEditForm<AddSpecialTopicFormFields>
            mode="add"
            form={form}
            axiosError={addSpecialTopicError}
            isLoading={isAddSpecialTopicLoading}
            onFinish={onAddTopicFinish}
            specialLessonsData={specialLessonsData}
            userEmployeeData={employeeData}
            handleSelectFormTopicDate={handleSelectFormTopicDate}
            isSpecialLessonsDataFetching={isSpecialLessonsDataFetching}
            isSpecialLessonsDataError={isSpecialLessonsDataError}
        />
    );
};
